// ===========================================
// Notifications React Query Hooks
// ===========================================
// Custom hooks for notification preferences management
// Supports email, SMS, and WhatsApp notifications

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  notificationsApi,
  UpdatePreferencesInput,
  UpdatePhoneInput,
  UpdateTypeSettingsInput,
  NotificationTypeSettings,
  SendTestMessageInput,
  NotificationChannel,
  VerifyPhoneCodeInput,
} from '../api/notifications.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const notificationKeys = {
  all: ['notifications'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
  extendedPreferences: () => [...notificationKeys.all, 'extended-preferences'] as const,
  serviceStatus: () => [...notificationKeys.all, 'service-status'] as const,
  logs: (params?: { limit?: number; channel?: NotificationChannel; userId?: string }) =>
    [...notificationKeys.all, 'logs', params] as const,
  stats: (params?: { startDate?: string; endDate?: string }) =>
    [...notificationKeys.all, 'stats', params] as const,
};

// ===========================================
// NOTIFICATION QUERIES
// ===========================================

/**
 * Get current user's notification preferences
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => notificationsApi.getPreferences(),
  });
}

// ===========================================
// NOTIFICATION MUTATIONS
// ===========================================

/**
 * Update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdatePreferencesInput) =>
      notificationsApi.updatePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      enqueueSnackbar('Preferences updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to update preferences';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reset notification preferences to defaults
 */
export function useResetNotificationPreferences() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => notificationsApi.resetToDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Preferences reset to defaults', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to reset preferences';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// EXTENDED PREFERENCES (SMS/WhatsApp)
// ===========================================

/**
 * Get extended notification preferences with type settings
 */
export function useExtendedNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.extendedPreferences(),
    queryFn: () => notificationsApi.getExtendedPreferences(),
  });
}

/**
 * Update extended notification preferences (including SMS/WhatsApp)
 */
export function useUpdateExtendedPreferences() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdatePreferencesInput) =>
      notificationsApi.updateExtendedPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Notification preferences updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to update preferences';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// PHONE NUMBER MANAGEMENT
// ===========================================

/**
 * Update phone number for SMS/WhatsApp
 */
export function useUpdatePhone() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdatePhoneInput) => notificationsApi.updatePhone(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Phone number updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to update phone number';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mark phone as verified
 */
export function useVerifyPhone() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => notificationsApi.verifyPhone(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Phone number verified', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to verify phone number';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Send a verification code via SMS to the user's phone
 */
export function useSendVerificationCode() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => notificationsApi.sendVerificationCode(),
    onSuccess: () => {
      enqueueSnackbar('Verification code sent', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to send verification code';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Verify phone with a 6-digit code
 */
export function useVerifyPhoneCode() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: VerifyPhoneCodeInput) => notificationsApi.verifyPhoneCode(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Phone number verified', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to verify phone number';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// WHATSAPP OPT-IN
// ===========================================

/**
 * Record WhatsApp opt-in
 */
export function useOptInWhatsApp() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => notificationsApi.optInWhatsApp(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('WhatsApp opt-in recorded', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to record WhatsApp opt-in';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// NOTIFICATION TYPE SETTINGS
// ===========================================

/**
 * Update channels for a specific notification type
 */
export function useUpdateTypeSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: UpdateTypeSettingsInput) =>
      notificationsApi.updateTypeSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('Notification settings updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to update notification settings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update all notification type settings at once
 */
export function useUpdateAllTypeSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (settings: NotificationTypeSettings) =>
      notificationsApi.updateAllTypeSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.extendedPreferences() });
      enqueueSnackbar('All notification settings updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to update notification settings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// TEST MESSAGES
// ===========================================

/**
 * Send test SMS
 */
export function useSendTestSms() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: SendTestMessageInput) => notificationsApi.sendTestSms(data),
    onSuccess: () => {
      enqueueSnackbar('Test SMS sent successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to send test SMS';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Send test WhatsApp message
 */
export function useSendTestWhatsApp() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: SendTestMessageInput) => notificationsApi.sendTestWhatsApp(data),
    onSuccess: () => {
      enqueueSnackbar('Test WhatsApp message sent successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to send test WhatsApp message';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// SERVICE STATUS
// ===========================================

/**
 * Get SMS/WhatsApp service availability status
 */
export function useNotificationServiceStatus() {
  return useQuery({
    queryKey: notificationKeys.serviceStatus(),
    queryFn: () => notificationsApi.getServiceStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ===========================================
// ADMIN: NOTIFICATION LOGS
// ===========================================

/**
 * Get notification delivery logs (Admin only)
 */
export function useNotificationLogs(params?: {
  limit?: number;
  channel?: NotificationChannel;
  userId?: string;
}) {
  return useQuery({
    queryKey: notificationKeys.logs(params),
    queryFn: () => notificationsApi.getLogs(params),
  });
}

/**
 * Get delivery statistics (Admin only)
 */
export function useNotificationDeliveryStats(params?: {
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    queryKey: notificationKeys.stats(params),
    queryFn: () => notificationsApi.getDeliveryStats(params),
  });
}
